import React, { useEffect } from 'react';
import {
  Drawer,
  Form,
  Input,
  Switch,
  Button,
  Space,
  Popconfirm,
  Divider,
  Typography,
  Tooltip,
} from 'antd';
import { useDispatch } from 'react-redux';
import {
  IndexFund,
  CreateIndexFund,
  createIndexFund,
  updateIndexFund,
  deleteIndexFund,
} from 'state';
import UsageCell, { SessionLite } from './UsageCell';
import { DECOMMISSION_HINT } from './FundsTable';

const { Text } = Typography;

type Mode = 'create' | 'edit';

type Props = {
  open: boolean;
  mode: Mode;
  fund: IndexFund | null;
  sessionsById: Record<number, SessionLite>;
  onClose: () => void;
  onShowEod: (fund: IndexFund) => void;
  onRefreshData: (fund: IndexFund) => void;
};

/**
 * Create / edit / detail drawer. Replaces the classic inline-row editing with a proper validated
 * form, and groups the per-fund secondary actions (EoD data, usage, lifecycle) beneath it. Create
 * mode shows only the details form (a new fund has no usage/lifecycle yet).
 */
const FundDrawer: React.FC<Props> = ({
  open,
  mode,
  fund,
  sessionsById,
  onClose,
  onShowEod,
  onRefreshData,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && fund) {
      form.setFieldsValue({
        ric: fund.ric,
        name: fund.name,
        issuer_name: fund.issuer_name,
        description: fund.description,
        asset_class: fund.asset_class,
        geography: fund.geography,
        fetch_eod: fund.fetch_eod,
      });
    } else {
      form.resetFields();
    }
  }, [open, mode, fund, form]);

  const inUse =
    (fund?.in_playing_sessions || []).length +
      (fund?.in_paused_sessions || []).length >
    0;

  const onFinish = () => {
    const v = form.getFieldsValue();
    if (mode === 'create') {
      const payload: CreateIndexFund = {
        ric: v.ric,
        issuer_name: v.issuer_name,
        name: v.name,
        description: v.description,
        asset_class: v.asset_class || '',
        geography: v.geography || '',
      };
      dispatch(createIndexFund(payload));
    } else if (fund) {
      dispatch(updateIndexFund({ ...fund, ...v }));
    }
    onClose();
  };

  const handleDelete = () => {
    if (fund) dispatch(deleteIndexFund(fund));
    onClose();
  };

  const handleDecommission = () => {
    if (fund) dispatch(updateIndexFund({ ...fund, decommissioned: true }));
    onClose();
  };

  return (
    <Drawer
      title={
        mode === 'create' ? 'Create index fund' : `Edit · ${fund?.ric ?? ''}`
      }
      width={460}
      open={open}
      onClose={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={() => form.submit()}>
            {mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          label="RIC"
          name="ric"
          rules={[{ required: true, message: 'required' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'required' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Issuer"
          name="issuer_name"
          rules={[{ required: true, message: 'required' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Description"
          name="description"
          rules={[{ required: true, message: 'required' }]}
        >
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} />
        </Form.Item>
        <Form.Item label="Asset class" name="asset_class">
          <Input placeholder="e.g. Equity, Fixed Income (optional)" />
        </Form.Item>
        <Form.Item label="Geography" name="geography">
          <Input placeholder="e.g. Global, Europe, US (optional)" />
        </Form.Item>
        <Form.Item label="Fetch EoD" name="fetch_eod" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>

      {mode === 'edit' && fund && (
        <>
          <Divider />
          <Text strong>EoD data</Text>
          <div style={{ marginTop: 8 }}>
            <Space>
              <Button onClick={() => onShowEod(fund)}>Show EoD</Button>
              <Button onClick={() => onRefreshData(fund)}>
                Refresh / import data
              </Button>
            </Space>
          </div>

          <Divider />
          <Text strong>Usage</Text>
          <div style={{ marginTop: 8 }}>
            <UsageCell fund={fund} sessionsById={sessionsById} />
          </div>

          <Divider />
          <Text strong>Lifecycle</Text>
          <div style={{ marginTop: 8 }}>
            <Space>
              <Popconfirm
                title="Decommission this fund?"
                description={
                  <div style={{ maxWidth: 300 }}>{DECOMMISSION_HINT}</div>
                }
                okText="Decommission"
                onConfirm={handleDecommission}
              >
                <Button>Decommission</Button>
              </Popconfirm>
              <Tooltip
                title={inUse ? 'In use by active sessions — cannot delete' : ''}
              >
                <Popconfirm
                  title="Permanently delete this fund?"
                  okText="Delete"
                  okButtonProps={{ danger: true }}
                  onConfirm={handleDelete}
                  disabled={inUse}
                >
                  <Button danger disabled={inUse}>
                    Delete
                  </Button>
                </Popconfirm>
              </Tooltip>
            </Space>
          </div>
        </>
      )}
    </Drawer>
  );
};

export default FundDrawer;
